import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Settings() {
  const { company, refresh } = useAuth();
  const [form, setForm] = useState({});
  const CURRENCY_OPTIONS = [
    { value: "INR", label: "Indian Rupee (₹)" },
    { value: "USD", label: "US Dollar ($)" },
    { value: "AUD", label: "Australian Dollar (A$)" },
    { value: "EUR", label: "Euro (€)" },
    { value: "GBP", label: "British Pound (£)" },
  ];
  const [saving, setSaving] = useState(false);
  const [backupSending, setBackupSending] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);

  const loadBackupHistory = async () => {
    try {
      const { data } = await api.get("/backup/history");
      setBackupHistory(data || []);
    } catch (err) {
      // ignore if user cannot access backup history
    }
  };

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || "", address: company.address || "", phone: company.phone || "",
        email: company.email || "", website: company.website || "", gst_number: company.gst_number || "",
        description: company.description || "", currency: company.currency || "INR", currency_symbol: company.currency_symbol || "₹",
        tax_rate: company.tax_rate ?? 18, gst_enabled: company.gst_enabled,
        invoice_prefix: company.invoice_prefix || "INV", terms_and_conditions: company.terms_and_conditions || "",
        payment_options: company.payment_options || [],
        logo_file_id: company.logo_file_id || null,
      });
      loadBackupHistory();
    }
  }, [company]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/company", { logo_file_id: data.file_id });
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) { toast.error(extractError(err)); }
    finally { setUploadingLogo(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company", { ...form, tax_rate: Number(form.tax_rate) || 0 });
      await refresh();
      toast.success("Settings saved");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  const sendBackup = async () => {
    setBackupSending(true);
    try {
      const { data } = await api.post("/backup/send");
      if (data.status === "sent") {
        toast.success("Backup email sent successfully");
      } else {
        toast.error(data.error || "Backup email failed");
      }
      await loadBackupHistory();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBackupSending(false);
    }
  };

  const downloadBackup = async () => {
    setBackupDownloading(true);
    try {
      const { data } = await api.get("/backup/export");
      const files = [
        { name: "clients.csv", content: data.clients_csv },
        { name: "bookings.csv", content: data.bookings_csv },
        { name: "invoices.csv", content: data.invoices_csv },
      ];
      files.forEach(({ name, content }) => {
        const blob = new Blob([content], { type: name.endsWith('.json') ? 'application/json;charset=utf-8;' : 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
      toast.success("Backup files downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBackupDownloading(false);
    }
  };

  const updatePO = (idx, key, val) => {
    const arr = [...(form.payment_options || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, payment_options: arr });
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Workspace</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Settings</h1>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="settings-tab-invoice">Invoice defaults</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments">Payment options</TabsTrigger>
          <TabsTrigger value="terms" data-testid="settings-tab-terms">Terms</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Company profile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-md border border-black/10 bg-[#F5F4F0] flex items-center justify-center overflow-hidden" data-testid="company-logo-preview">
                  {company?.logo_file_id ? (
                    <img src={`${BACKEND_URL}/api/files/${company.logo_file_id}`} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-xs text-zinc-500">No logo</span>
                  )}
                </div>
                <div className="flex-1">
                  <Label>Company logo</Label>
                  <div className="mt-2 flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} data-testid="company-logo-input" />
                      <span className="inline-flex items-center px-4 h-10 rounded-md border border-black/10 bg-white text-sm hover:bg-black/[0.03]">
                        {uploadingLogo ? "Uploading…" : "Upload logo"}
                      </span>
                    </label>
                    {company?.logo_file_id && (
                      <Button type="button" variant="ghost" size="sm" className="text-red-700 hover:bg-red-50" onClick={async () => { await api.patch("/company", { logo_file_id: null }); await refresh(); toast.success("Logo removed"); }} data-testid="company-logo-remove-btn">Remove</Button>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">PNG, JPG or WEBP up to 8 MB. Appears on invoices.</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Company name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name-input" /></Field>
                <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="GST number"><Input value={form.gst_number || ""} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} /></Field>
                <Field label="Website" className="md:col-span-2"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
                <Field label="Address" className="md:col-span-2"><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                <Field label="Description" className="md:col-span-2"><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoice" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Invoice defaults</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Currency"><Select value={form.currency || "INR"} onValueChange={(value) => setForm({ ...form, currency: value })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></Field>
                <Field label="Currency symbol"><Input value={form.currency_symbol || ""} onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })} /></Field>
                <Field label="Tax rate (%)"><Input type="number" value={form.tax_rate ?? ""} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="settings-tax-input" /></Field>
                <Field label="Invoice prefix"><Input value={form.invoice_prefix || ""} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} /></Field>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Payment options</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(form.payment_options || []).map((po, i) => (
                <div key={po.id || i} className="border border-black/10 rounded p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`payment-option-${i}`}>
                  <Field label="Display name"><Input value={po.name} onChange={(e) => updatePO(i, "name", e.target.value)} /></Field>
                  <Field label="Account details (internal)"><Input value={po.details || ""} onChange={(e) => updatePO(i, "details", e.target.value)} /></Field>
                  <Field label="Instructions (shown on invoice)" className="md:col-span-2"><Input value={po.instructions || ""} onChange={(e) => updatePO(i, "instructions", e.target.value)} /></Field>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="terms" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Terms & conditions</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={10} value={form.terms_and_conditions || ""} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} data-testid="settings-terms-input" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Main save button for profile/invoice/payments/terms tabs */}
      {/* Only show if not on webhooks tab - webhook tab has its own save button */}

      {/* Main save button for profile/invoice/payments/terms tabs */}
      {/* Only show if not on webhooks tab - webhook tab has its own save button */}
      <div className="flex justify-end">
        <Button disabled={saving} onClick={save} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full px-6" data-testid="settings-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Workspace backup</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-zinc-700">Download or email a backup export of your company clients, bookings, and invoices.</div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={sendBackup} disabled={backupSending}>
              {backupSending ? "Sending…" : "Send backup email"}
            </Button>
            <Button variant="outline" onClick={downloadBackup} disabled={backupDownloading}>
              {backupDownloading ? "Downloading…" : "Download backup"}
            </Button>
          </div>
          {backupHistory.length > 0 && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-zinc-900">Recent backup history</div>
              {backupHistory.slice(0, 8).map((item, idx) => (
                <div key={idx} className="rounded border border-black/10 p-3 text-sm text-zinc-700">
                  <div className="flex items-center justify-between gap-2">
                    <span>{item.company_name || item.company_id}</span>
                    <span className={item.status === "sent" ? "text-emerald-700" : "text-rose-700"}>{item.status}</span>
                  </div>
                  <div className="text-xs mt-1">{item.sent_at ? new Date(item.sent_at).toLocaleString() : "—"}</div>
                  {item.error && <div className="text-xs text-rose-700 mt-1">{item.error}</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
